import React, { useMemo } from 'react';
import { string, shape, arrayOf, array } from 'prop-types';

import { useBLazyRevalidate } from 'hooks';
import {
    BrandSimpleCard,
    BrandDownloadCard,
    VideoCard,
    NewsCard
} from 'components/cards';

import { getLocalization } from '../utils';
import { suggestionsTypes, SUGGESTIONS_CONTAINER_ID } from '../constants';

const SearchSuggestions = ({ suggestions }) => {
    const { product, additives, technology, video, article, document, other } = suggestionsTypes;

    useBLazyRevalidate(SUGGESTIONS_CONTAINER_ID, [suggestions]);

    const nonEmptySuggestionsGroups = useMemo(() => {
        if (!suggestions || !suggestions.length) {
            return [];
        }

        const filteredSuggestions = suggestions.filter(
            suggestion => suggestion.groups && !!suggestion.groups.length
        );

        return filteredSuggestions.map(item => {
            return {
                ...item,
                groups: item.groups.filter(
                    group => group.items && group.items.length
                )
            };
        });
    }, [suggestions]);

    const renderResultItemsByType = (type, items, isTwoColsLayout) => {
        if (!items || !items.length) {
            return null;
        }

        switch (type) {
            case product:
                return items.map(({ id, title, url }) => {
                    if (isTwoColsLayout) {
                        return (
                            <div className="col-sm-6" key={id}>
                                <BrandSimpleCard title={title} url={url} />
                            </div>
                        );
                    }
                    return (
                        <div className="col-sm-6 col-md-3" key={id}>
                            <BrandSimpleCard title={title} url={url} />
                        </div>
                    );
                });
            case additives:
                return items.map(({ id, title, url }) => {
                    if (isTwoColsLayout) {
                        return (
                            <div className="col-sm-6" key={id}>
                                <BrandSimpleCard title={title} url={url} />
                            </div>
                        );
                    }
                    return (
                        <div className="col-sm-6 col-md-3" key={id}>
                            <BrandSimpleCard title={title} url={url} />
                        </div>
                    );
                });
            case technology:
                return items.map(
                    ({
                        id,
                        title,
                        url,
                        target
                    }) => (
                        <div className="col-sm-6 col-md-3" key={id}>
                            <BrandSimpleCard title={title} url={url} target={target} />
                        </div>
                    )
                );
            case video:
                return items.map(
                    ({
                        badges,
                        buttonTitle,
                        estimate,
                        language,
                        description,
                        date,
                        dateTitle,
                        filesCount,
                        filesTitle,
                        id,
                        thumbnailAlt,
                        title,
                        url,
                        previewImage
                    }) => (
                        <div className="col-sm-6 col-md-3" key={id}>
                            <VideoCard
                                badges={badges}
                                thumbnailAlt={thumbnailAlt}
                                estimate={estimate}
                                language={language}
                                date={date}
                                dateTitle={dateTitle}
                                description={description}
                                title={title}
                                filesCount={filesCount}
                                filesTitle={filesTitle}
                                url={url}
                                buttonTitle={buttonTitle}
                                previewImage={previewImage}
                            />
                        </div>
                    )
                );

            case article:
                return items.map(
                    ({
                        id,
                        url,
                        title,
                        image,
                        date,
                        dateTitle,
                        buttonTitle,
                        perex
                    }) => (
                        <div className="col-sm-6 col-md-4" key={id}>
                            <NewsCard
                                url={url}
                                title={title}
                                image={image}
                                date={date}
                                dateTitle={dateTitle}
                                buttonTitle={buttonTitle}
                                perex={perex}
                            />
                        </div>
                    )
                );

            case document:
                return items.map(
                    ({
                        id,
                        blockedTitle,
                        downloadTitle,
                        url,
                        blocked,
                        title,
                        size
                    }) => (
                        <div className="col-sm-6 col-md-4" key={id}>
                            <BrandDownloadCard
                                blocked={blocked}
                                url={url}
                                title={title}
                                size={size}
                                blockedTitle={blockedTitle}
                                downloadTitle={downloadTitle}
                            />
                        </div>
                    )
                );

            case other:
                return (
                    <div className="col-12 serp-links">
                        {items.map(({ link, subLink, id }) => (
                            <div className="serp-links__item" key={id}>
                                {!!link && !!link.url && (
                                    <div className="serp-links__item-link">
                                        <a
                                            className="link
                            link--large"
                                            href={link.url}
                                        >
                                            {link.title}
                                        </a>
                                    </div>
                                )}
                                {!!subLink && !!subLink.url && (
                                    <div className="serp-links__item-link">
                                        <a
                                            className="link
                            link--small"
                                            href={subLink.url}
                                        >
                                            {subLink.title}
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                );

            default:
                return null;
        }
    };

    if (!suggestions) {
        return null;
    }

    return (
        <div className="c-search__suggestions">
            {nonEmptySuggestionsGroups.length ? (
                nonEmptySuggestionsGroups.map(({ id, groups, type }) => (
                    <div key={id} className="c-search__suggestions-section">
                        {groups.length > 1 ? (
                            <div className="grid">
                                {groups.map(
                                    ({
                                        itemsAmount,
                                        id: groupId,
                                        items,
                                        title,
                                        url
                                    }) => (
                                        <div
                                            className="col-md-6 c-search__suggestions-group"
                                            key={groupId}
                                        >
                                            <h2 className="c-search__suggestions-section-title h4">
                                                <a
                                                    href={url}
                                                    className="c-search__suggestions-section-title-link"
                                                >
                                                    {title}{' '}
                                                    <span className="results-amount">
                                                        ({`${itemsAmount}`})
                                                    </span>
                                                </a>
                                            </h2>
                                            <div className="grid grid--cards grid--equal-height">
                                                {renderResultItemsByType(
                                                    type,
                                                    items,
                                                    groups.length > 1
                                                )}
                                            </div>
                                            <div className="c-search__suggestions-section-link">
                                                <a href={url} className="link">
                                                    {getLocalization(
                                                        'search/suggestions/section/link'
                                                    )}
                                                </a>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        ) : (
                            groups.map(
                                ({
                                    itemsAmount,
                                    id: groupId,
                                    items,
                                    title,
                                    url
                                }) => (
                                    <React.Fragment key={groupId}>
                                        <h2 className="c-search__suggestions-section-title h4">
                                            <a
                                                href={url}
                                                className="c-search__suggestions-section-title-link"
                                            >
                                                {title}{' '}
                                                <span className="results-amount">
                                                    ({`${itemsAmount}`})
                                                </span>
                                            </a>
                                        </h2>
                                        <div
                                            className={`grid grid--cards grid--equal-height ${
                                                type === video ||
                                                type === article
                                                    ? 'grid--cards--large-v-gutter'
                                                    : ''
                                            }`}
                                        >
                                            {renderResultItemsByType(
                                                type,
                                                items,
                                                groups.length > 1
                                            )}
                                        </div>
                                        <div className="c-search__suggestions-section-link">
                                            <a href={url} className="link">
                                                {getLocalization(
                                                    'search/suggestions/section/link'
                                                )}
                                            </a>
                                        </div>
                                    </React.Fragment>
                                )
                            )
                        )}
                    </div>
                ))
            ) : (
                <div className="c-search__suggestions-no-results">
                    {getLocalization('search/noResults')}
                </div>
            )}
        </div>
    );
};

SearchSuggestions.propTypes = {
    suggestions: arrayOf(
        shape({ id: string, title: string, items: array, url: string })
    ).isRequired
};

export default SearchSuggestions;
